// 思想マップ画面（メモから抽出した関心の地形）
import {
  escapeHtml,
  screenHead,
  showScreen,
  showLoading,
  hideLoading,
  toast,
  renderHistorySection,
  bindHistoryHandlers,
  renderBookFilter,
  getFilterState,
  applyBookFilter,
  bindFilterCounter,
} from '../ui.js';
import { fetchThoughtMap, fetchAllNotesParallel } from '../api.js';
import {
  getAllBooks,
  upsertBooks,
  getAiCache,
  setAiCache,
  saveAiHistory,
  getAiHistoryByType,
} from '../cache.js';

export async function showThoughtMapScreen() {
  const histItems = await getAiHistoryByType('map');
  if (histItems.length > 0) {
    return renderEntry(histItems);
  }
  return runDirect();
}

async function renderEntry(histItems) {
  const books = await getAllBooks();
  const html = `
    ${screenHead('思想マップ')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      全書籍のメモから「関心の地形」を抽出します。<br/>
      フィルターで対象書籍を絞ることもできます（例: 2025年だけ／写真ジャンルだけ）。
    </p>
    ${renderBookFilter()}
    <button class="btn btn--ink btn--block" id="btn-go" type="button">
      <svg class="icon"><use href="#i-sparkles"/></svg>
      <span>新規に抽出する</span>
    </button>
    ${renderHistorySection(histItems)}
  `;
  const screen = showScreen('thought-map', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  bindFilterCounter(screen, books);
  screen.querySelector('#btn-go').addEventListener('click', () => runDirect(true, getFilterState(screen)));
  bindHistoryHandlers(
    screen,
    histItems,
    (entry) => renderResult(entry.payload, entry.context?.n || 0),
    () => showThoughtMapScreen()
  );
}

async function runDirect(forceNew = false, filterState = null) {
  let books = await getAllBooks();
  if (filterState) {
    const filtered = applyBookFilter(books, filterState);
    if (filtered.length) books = filtered;
  }
  const missingCount = books.filter((b) => !b.note).length;
  if (missingCount > 0) {
    showLoading(`書籍メモを補完中… (0/${missingCount})`);
    await fetchAllNotesParallel(books, (done, total) => {
      const ov = document.getElementById('overlay-msg');
      if (ov) ov.textContent = `書籍メモを補完中… (${done}/${total})`;
    });
    await upsertBooks(books);
    hideLoading();
  }
  const booksWithNotes = books.filter((b) => (b.note || '').trim().length > 30);

  if (booksWithNotes.length < 3) {
    showScreen(
      'thought-map',
      `${screenHead('思想マップ')}<p class="empty">メモのある書籍が少なすぎます（${booksWithNotes.length} 冊）。書籍を 3 冊以上 同期してメモを書いてからお試しください。</p>`
    );
    document.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
    return;
  }

  const cacheKey = `map:${booksWithNotes.length}:${booksWithNotes[0]?.updatedAt || ''}`;
  let data = forceNew ? null : await getAiCache(cacheKey);
  const fromCache = !!data;

  if (!data) {
    showLoading('メモから関心の地形を抽出中…');
    try {
      data = await fetchThoughtMap(booksWithNotes);
      await setAiCache(cacheKey, data);
    } catch (e) {
      hideLoading();
      toast('抽出に失敗しました');
      return;
    } finally {
      hideLoading();
    }
  }
  if (!fromCache) {
    await saveAiHistory({
      type: 'map',
      title: `思想マップ（${data.clusters?.length || 0} クラスタ）`,
      subtitle: data.clusters?.slice(0, 3).map((c) => c.name).join(' / '),
      context: { n: booksWithNotes.length },
      payload: data,
    });
  }
  renderResult(data, booksWithNotes.length);
}

export function renderFromHistory(entry) {
  renderResult(entry.payload, entry.context?.n || 0);
}

function renderResult(data, n) {
  const clusters = data.clusters || [];
  const maxWeight = Math.max(1, ...clusters.flatMap((c) => (c.keywords || []).map((k) => k.weight)));

  // ボタン data-* に term を、AI が返した books は JSON でクラスタ単位に保持
  // クリック時に IndexedDB の本と書名でゆるく照合し、関連書籍カードを描画する
  const html = `
    ${screenHead('思想マップ')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 24px;">
      ${n} 冊のメモから ${clusters.length} のクラスタを抽出しました。<br/>
      キーワードをタップすると、その主題で読んだ本と次のアクションが表示されます。
    </p>
    ${clusters
      .map(
        (c, ci) => `
      <div class="map-cluster" data-cluster-index="${ci}">
        <h3 class="map-cluster__title">${escapeHtml(c.name || '')}</h3>
        <p class="map-cluster__summary">${escapeHtml(c.summary || '')}</p>
        <div class="map-keywords">
          ${(c.keywords || [])
            .map((k, ki) => {
              const size = 11 + Math.round(((k.weight || 1) / maxWeight) * 6);
              return `<button type="button" class="map-keyword" data-kw-cluster="${ci}" data-kw-index="${ki}" data-kw-term="${escapeHtml(k.term)}" style="font-size:${size}px;">${escapeHtml(k.term)}</button>`;
            })
            .join('')}
        </div>
        <div class="map-keyword-panel" data-panel-cluster="${ci}" hidden></div>
      </div>
    `
      )
      .join('')}
    <button class="btn btn--block btn--ghost" id="btn-regen" type="button" style="margin-top:24px;">
      <svg class="icon"><use href="#i-refresh"/></svg>
      <span>最新のメモから再構築する</span>
    </button>
  `;
  const screen = showScreen('thought-map', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-regen').addEventListener('click', () => runDirect(true));
  bindKeywordTaps(screen, clusters);
}

async function bindKeywordTaps(screen, clusters) {
  const allBooks = await getAllBooks();
  screen.querySelectorAll('.map-keyword').forEach((btn) => {
    btn.addEventListener('click', () => {
      const ci = Number(btn.dataset.kwCluster);
      const ki = Number(btn.dataset.kwIndex);
      const cluster = clusters[ci];
      const k = (cluster?.keywords || [])[ki];
      if (!k) return;
      const panel = screen.querySelector(`[data-panel-cluster="${ci}"]`);
      const wasActive = btn.classList.contains('map-keyword--active');
      // 同じクラスタ内の他のキーワードを非アクティブに
      screen.querySelectorAll(`[data-kw-cluster="${ci}"]`).forEach((b) => b.classList.remove('map-keyword--active'));
      if (wasActive) {
        panel.hidden = true;
        panel.innerHTML = '';
        return;
      }
      btn.classList.add('map-keyword--active');
      panel.hidden = false;
      panel.innerHTML = renderKeywordPanel(k, allBooks);
      bindPanelActions(panel, k, allBooks);
    });
  });
}

// AI が返した k.books（書名配列）を、IndexedDB の books とゆるく照合
function matchBooks(termBooks, allBooks) {
  const matched = [];
  for (const name of termBooks || []) {
    const target = String(name).trim();
    if (!target) continue;
    let hit = allBooks.find((b) => (b.name || '').trim() === target);
    if (!hit) {
      // 部分一致（前後5字程度のゆらぎを許容）
      hit = allBooks.find(
        (b) => (b.name || '').includes(target) || target.includes((b.name || '').trim())
      );
    }
    if (hit && !matched.find((m) => m.id === hit.id)) matched.push(hit);
  }
  return matched;
}

function renderKeywordPanel(k, allBooks) {
  const related = matchBooks(k.books, allBooks);
  const relatedHtml = related.length
    ? `<ul style="list-style:none;padding:0;margin:0;border-top:1px solid var(--line);">
         ${related
           .map(
             (b) => `
           <li data-related-book="${escapeHtml(b.id)}" style="display:flex;align-items:baseline;justify-content:space-between;gap:8px;padding:8px 0;border-bottom:1px solid var(--line);cursor:pointer;">
             <div style="flex:1;min-width:0;">
               <p style="margin:0;font-family:var(--font-serif);font-size:14px;font-weight:500;color:var(--ink);">${escapeHtml(b.name)}</p>
               <p style="margin:2px 0 0;font-size:11px;color:var(--mute);">${escapeHtml(b.author || '')}</p>
             </div>
             <span style="font-family:var(--font-mono);font-size:10px;letter-spacing:0.12em;color:var(--mute);">${escapeHtml(b.status || '')}</span>
           </li>
         `
           )
           .join('')}
       </ul>`
    : `<p style="margin:0;padding:10px 0;font-size:12px;color:var(--mute);">このキーワードに直接紐づいた既読本は見つかりませんでした。</p>`;

  return `
    <div style="margin-top:12px;padding:12px 14px;border:1px solid var(--line);background:var(--paper);">
      <div style="display:flex;align-items:baseline;gap:10px;margin-bottom:8px;">
        <span style="font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">keyword</span>
        <span style="font-family:var(--font-serif);font-size:15px;font-weight:500;">${escapeHtml(k.term)}</span>
        ${k.weight ? `<span style="font-family:var(--font-mono);font-size:10px;color:var(--mute);">weight ${k.weight}</span>` : ''}
      </div>
      <p style="margin:0 0 10px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">this theme · ${related.length} books</p>
      ${relatedHtml}
      <div style="display:flex;flex-direction:column;gap:6px;margin-top:14px;">
        <button class="btn btn--small btn--block btn--ink" data-act="keyword-search" type="button">
          <svg class="icon"><use href="#i-key"/></svg>
          <span>この主題で新しい本を探す</span>
        </button>
        <button class="btn btn--small btn--block btn--ghost" data-act="semantic-search" type="button">
          <svg class="icon"><use href="#i-search"/></svg>
          <span>この主題でメモを意味検索</span>
        </button>
      </div>
    </div>
  `;
}

function bindPanelActions(panel, k, allBooks) {
  // 関連書籍タップ → 書籍詳細
  panel.querySelectorAll('[data-related-book]').forEach((li) => {
    li.addEventListener('click', async () => {
      const id = li.dataset.relatedBook;
      window.history.pushState({ screen: 'book-detail' }, '', `#book/${id}`);
      const { showBookDetailScreen } = await import('./book-detail.js');
      showBookDetailScreen(id);
    });
  });
  panel.querySelector('[data-act="keyword-search"]')?.addEventListener('click', async () => {
    window.history.pushState({ screen: 'keyword-search' }, '', '#keyword-search');
    const { showKeywordSearchScreen } = await import('./keyword-search.js');
    showKeywordSearchScreen({ initialKeyword: k.term });
  });
  panel.querySelector('[data-act="semantic-search"]')?.addEventListener('click', async () => {
    window.history.pushState({ screen: 'semantic-search' }, '', '#semantic-search');
    const { showSemanticSearchScreen } = await import('./semantic-search.js');
    showSemanticSearchScreen({ initialQuery: k.term });
  });
}
